import React from 'react';
import { useApp } from '../context/AppContext';
import api, { API } from '../lib/api';
import { ROLES } from '../mock';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from '../hooks/use-toast';
import { UserPlus, Trash2, Shield, Building2, UserCheck, AlertTriangle, Info, ExternalLink } from 'lucide-react';
import BulkInviteButton from '../components/BulkInviteButton';

const roleBadge = {
  [ROLES.ADMIN]: { label: 'Admin', className: 'bg-brand-indigo text-white' },
  [ROLES.SALES_HEAD]: { label: 'Sales Head', className: 'bg-brand-cyan text-white' },
  [ROLES.RM]: { label: 'RM', className: 'bg-brand-green text-white' },
};

const emptyForm = { email: '', name: '', role: ROLES.RM, title: '', euin: '', arn: 'ARN-47282', sales_head_id: '', rm_ids: [] };

const UsersAdminPage = () => {
  const { refreshData } = useApp();
  const [users, setUsers] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [form, setForm] = React.useState(emptyForm);
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(async () => {
    try { const list = await api.listUsers(); setUsers(list); } catch { /* noop */ }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const salesHeads = users.filter((u) => u.role === ROLES.SALES_HEAD);
  const rms = users.filter((u) => u.role === ROLES.RM);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !form.name.trim()) { toast({ title: 'Email and name are required' }); return; }
    setBusy(true);
    try {
      const body = { ...form, email: form.email.trim().toLowerCase() };
      if (body.role !== ROLES.SALES_HEAD) delete body.rm_ids;
      if (body.role !== ROLES.RM) delete body.sales_head_id;
      const r = await fetch(`${API}/users`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || 'Failed');
      }
      toast({ title: 'User added', description: `${form.name} can now sign in.` });
      setOpen(false); setForm(emptyForm);
      await load(); await refreshData();
    } catch (err) {
      toast({ title: 'Add failed', description: err.message });
    } finally { setBusy(false); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.name} (${u.email})? This cannot be undone.`)) return;
    try {
      const r = await fetch(`${API}/users/${u.user_id}`, { method: 'DELETE', credentials: 'include' });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || 'Failed');
      }
      toast({ title: 'User removed' });
      await load(); await refreshData();
    } catch (err) {
      toast({ title: 'Remove failed', description: err.message });
    }
  };

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-[1200px] mx-auto">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Admin</div>
          <h1 className="text-3xl font-bold text-brand-indigo">User Management</h1>
          <p className="text-sm text-muted-foreground mt-1">Provision Admin, Sales Head and RM accounts. Emails must be under @aktawealth.com.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-brand-indigo hover:bg-brand-indigoLight text-white"><UserPlus className="h-4 w-4 mr-2" /> Add user</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader><DialogTitle>Add internal user</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label>Full name</Label>
                  <Input value={form.name} onChange={(e) => set('name', e.target.value)} className="mt-1 h-10" placeholder="Priya Shah" />
                </div>
                <div className="col-span-2">
                  <Label>Work email</Label>
                  <Input value={form.email} onChange={(e) => set('email', e.target.value)} className="mt-1 h-10" placeholder="priya@aktawealth.com" />
                </div>
                <div>
                  <Label>Role</Label>
                  <Select value={form.role} onValueChange={(v) => set('role', v)}>
                    <SelectTrigger className="mt-1 h-10"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ROLES.ADMIN}>Admin</SelectItem>
                      <SelectItem value={ROLES.SALES_HEAD}>Sales Head</SelectItem>
                      <SelectItem value={ROLES.RM}>Relationship Manager</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Title (optional)</Label>
                  <Input value={form.title} onChange={(e) => set('title', e.target.value)} className="mt-1 h-10" placeholder="e.g. Retail Sales Head — Gujarat South" />
                </div>
                {form.role === ROLES.RM && (
                  <>
                    <div>
                      <Label>EUIN</Label>
                      <Input value={form.euin} onChange={(e) => set('euin', e.target.value)} className="mt-1 h-10" placeholder="E123456" />
                    </div>
                    <div>
                      <Label>Reports to</Label>
                      <Select value={form.sales_head_id} onValueChange={(v) => set('sales_head_id', v)}>
                        <SelectTrigger className="mt-1 h-10"><SelectValue placeholder="Select Sales Head" /></SelectTrigger>
                        <SelectContent>
                          {salesHeads.map((sh) => (
                            <SelectItem key={sh.user_id} value={sh.user_id}>{sh.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}
                {form.role === ROLES.SALES_HEAD && (
                  <div className="col-span-2">
                    <Label>Mapped RMs</Label>
                    <div className="mt-1 border border-brand-border rounded-md p-2 max-h-40 overflow-y-auto space-y-1">
                      {rms.length === 0 && <div className="text-xs text-muted-foreground p-2">No RMs yet. Add RMs first, then edit their Sales Head assignment.</div>}
                      {rms.map((rm) => {
                        const checked = (form.rm_ids || []).includes(rm.user_id);
                        return (
                          <label key={rm.user_id} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-brand-bg cursor-pointer">
                            <Checkbox checked={checked} onCheckedChange={(v) => {
                              set('rm_ids', v ? [...(form.rm_ids || []), rm.user_id] : (form.rm_ids || []).filter((x) => x !== rm.user_id));
                            }} />
                            <div className="text-sm text-brand-indigo">{rm.name} <span className="text-[11px] text-muted-foreground">· {rm.email}</span></div>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 text-amber-900 px-3 py-2 text-[11px]">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5" /> Only @aktawealth.com Google accounts can sign in. Ensure the email above matches the user’s Google login exactly.
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={busy} className="bg-brand-indigo text-white">{busy ? 'Saving\u2026' : 'Add user'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-brand-cyan/40 bg-gradient-to-r from-brand-bg to-white">
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-md bg-brand-cyan text-white flex items-center justify-center shrink-0"><Info className="h-5 w-5" /></div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-brand-indigo">Test the live Google Sign-in path</div>
              <ol className="mt-2 space-y-1 text-xs text-brand-indigo/85 leading-relaxed list-decimal ml-4">
                <li>Click <strong>Add user</strong> and enter your own Google-linked <code>@aktawealth.com</code> email, name and role.</li>
                <li>Sign out of this admin session using the logout icon in the sidebar.</li>
                <li>On the login screen, click <strong>Continue with Google</strong> and sign in with the same Google account.</li>
                <li>You will land on the dashboard that matches the role you just assigned yourself.</li>
              </ol>
              <a href="/login" target="_blank" rel="noopener" className="inline-flex items-center gap-1 mt-3 text-xs font-semibold text-brand-indigo hover:underline">Open sign-in page in a new tab <ExternalLink className="h-3 w-3" /></a>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-brand-border">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-brand-border">
                  <th className="px-5 py-3 font-semibold">Name</th>
                  <th className="px-5 py-3 font-semibold">Email</th>
                  <th className="px-5 py-3 font-semibold">Role</th>
                  <th className="px-5 py-3 font-semibold">Details</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const badge = roleBadge[u.role] || { label: u.role, className: 'bg-muted' };
                  const details = u.role === ROLES.RM
                    ? `EUIN ${u.euin || '—'} · Reports to ${salesHeads.find((s) => s.user_id === u.sales_head_id)?.name || '—'}`
                    : u.role === ROLES.SALES_HEAD
                    ? `Maps to ${(u.rm_ids || []).map((id) => rms.find((r) => r.user_id === id)?.name).filter(Boolean).join(', ') || 'no RMs'}`
                    : (u.title || '—');
                  return (
                    <tr key={u.user_id} className="border-b border-brand-border/50 hover:bg-brand-bg">
                      <td className="px-5 py-3 font-semibold text-brand-indigo">{u.name}</td>
                      <td className="px-5 py-3 text-muted-foreground">{u.email}</td>
                      <td className="px-5 py-3"><span className={`inline-block text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full ${badge.className}`}>{badge.label}</span></td>
                      <td className="px-5 py-3 text-xs text-muted-foreground">{details}</td>
                      <td className="px-5 py-3 text-right">
                        <Button variant="ghost" size="sm" onClick={() => remove(u)} className="text-brand-red hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <div className="text-[11px] text-muted-foreground leading-relaxed">
        Note: RMs cannot be deleted while they own client records. Reassign or remove clients first.
      </div>
    </div>
  );
};

export default UsersAdminPage;
